/**
 * REST client for docs/API.md (base /api/v1, Bearer JWT). Plain fetch, no React.
 *
 * Every failure is one of:
 *  - NetworkError  the request never got an HTTP answer (offline, DNS, timeout, server down)
 *  - ApiError      the server answered with a non-2xx status; `code` is the API's `error` field
 */
import type { SectionName } from "@npac/rules";
import type { ApiErrorBody, Assembly, Booth, BoothLocation, ServerSection, User } from "./types";

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class NetworkError extends Error {
  readonly kind = "network" as const;
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "NetworkError";
  }
}

export class ApiError extends Error {
  readonly kind = "api" as const;
  constructor(readonly status: number, readonly code: string, message: string, readonly body: ApiErrorBody | null) {
    super(message);
    this.name = "ApiError";
  }

  /** The API's Hindi message, if it sent one (`messageHi`). */
  get messageHi(): string | undefined {
    const m = this.body?.messageHi;
    return typeof m === "string" && m ? m : undefined;
  }

  /** `details` of a 400 validation error as [{ path, message }]. */
  get details(): { path: string; message: string }[] {
    const d = this.body?.details;
    if (!Array.isArray(d)) return [];
    return d
      .filter((x): x is { path?: unknown; message?: unknown } => !!x && typeof x === "object")
      .map((x) => ({ path: String(x.path ?? ""), message: String(x.message ?? "") }));
  }
}

export interface ApiClientOptions {
  /** e.g. http://10.0.2.2:4000/api/v1 (no trailing slash needed) */
  baseUrl: string | (() => string);
  getToken: () => Promise<string | null> | string | null;
  fetch?: FetchLike;
  /** per-request timeout; rural networks are slow, so be generous */
  timeoutMs?: number;
}

export interface SaveSectionBody {
  data: unknown;
  baseVersion?: number;
  deviceId?: string;
  force?: boolean;
}

export interface LocationBody {
  lat: number;
  lng: number;
  accuracyM?: number;
  capturedAt?: string;
  deviceId?: string;
}

export interface BoothDetail {
  booth: Booth;
  sections: Partial<Record<SectionName, ServerSection>>;
}

/** the login's second step: a code on N-PAC's / the candidate's number (admins only) */
export interface OtpStep {
  otpRequired: true;
  challengeId: string;
  /** masked, e.g. 98xxxxxx06 */
  to: string;
  channel: "whatsapp" | "sms" | "dev";
  expiresIn: number;
  devCode?: string;
  user: { name: string; role: string };
}

export class ApiClient {
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;

  constructor(private readonly opts: ApiClientOptions) {
    this.fetchImpl = opts.fetch ?? ((input, init) => fetch(input, init));
    this.timeoutMs = opts.timeoutMs ?? 30_000;
  }

  get baseUrl(): string {
    const b = typeof this.opts.baseUrl === "function" ? this.opts.baseUrl() : this.opts.baseUrl;
    return b.replace(/\/+$/, "");
  }

  async request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (auth) {
      const token = await this.opts.getToken();
      if (!token) throw new ApiError(401, "unauthenticated", "Not logged in", null);
      headers.Authorization = `Bearer ${token}`;
    }

    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller?.abort();
    }, this.timeoutMs);

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller?.signal,
      });
    } catch (e) {
      throw new NetworkError(timedOut ? "Request timed out" : e instanceof Error ? e.message : "Network request failed", e);
    } finally {
      clearTimeout(timer);
    }

    let text = "";
    try {
      text = await res.text();
    } catch (e) {
      throw new NetworkError("Connection lost while reading the response", e);
    }
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }

    if (!res.ok) {
      const b = json && typeof json === "object" ? (json as ApiErrorBody) : null;
      const code = b && typeof b.error === "string" ? b.error : res.status >= 500 ? "server_error" : `http_${res.status}`;
      const message = b && typeof b.message === "string" ? b.message : `HTTP ${res.status}`;
      throw new ApiError(res.status, code, message, b);
    }
    if (json === null) {
      // A 2xx that is not JSON is almost always a captive portal / proxy page, not our API.
      throw new NetworkError(`Unexpected response from server (HTTP ${res.status})`);
    }
    return json as T;
  }

  /* ---------------- endpoints ---------------- */

  /**
   * `login` = email or 10-digit mobile (+91 / 0 / spaces accepted by the API).
   * `client: "field"` gives surveyors the 48-hour polling-day session.
   */
  login(login: string, password: string): Promise<{ token: string; user: User } | OtpStep> {
    return this.request("POST", "/auth/login", { login: login.trim(), password, client: "field" }, false);
  }

  /** the second step of an admin's login (N-PAC, 4 Nov 2026): the code → the token */
  otpVerify(challengeId: string, code: string): Promise<{ token: string; user: User }> {
    return this.request("POST", "/auth/otp/verify", { challengeId, code, client: "field" }, false);
  }

  otpResend(challengeId: string): Promise<OtpStep> {
    return this.request("POST", "/auth/otp/resend", { challengeId }, false);
  }

  me(): Promise<{ user: User }> {
    return this.request("GET", "/me");
  }

  async assemblies(): Promise<Assembly[]> {
    return (await this.request<{ assemblies: Assembly[] }>("GET", "/assemblies")).assemblies;
  }

  async booths(assemblyId: string): Promise<Booth[]> {
    return (await this.request<{ booths: Booth[] }>("GET", `/assemblies/${enc(assemblyId)}/booths`)).booths;
  }

  async booth(assemblyId: string, code: string): Promise<BoothDetail> {
    const r = await this.request<{ booth: Booth; sections: Partial<Record<SectionName, ServerSection>> }>(
      "GET",
      `/assemblies/${enc(assemblyId)}/booths/${enc(code)}`
    );
    return { booth: r.booth, sections: r.sections ?? {} };
  }

  async saveSection(assemblyId: string, code: string, section: SectionName, body: SaveSectionBody): Promise<ServerSection> {
    const r = await this.request<{ section: ServerSection }>(
      "PUT",
      `/assemblies/${enc(assemblyId)}/booths/${enc(code)}/sections/${enc(section)}`,
      body
    );
    return r.section;
  }

  shareLocation(assemblyId: string, code: string, body: LocationBody): Promise<{ location: BoothLocation; mapsUrl: string }> {
    return this.request("POST", `/assemblies/${enc(assemblyId)}/booths/${enc(code)}/location`, body);
  }
}

const enc = encodeURIComponent;
